import axios from 'axios';
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { RootState } from '../../store';
import { ENV_CONFIG } from '../../shared/config/env.config';
import { toast } from 'react-toastify';
import { fetchOAuthKey } from '../../shared/services/oauthService';
import { recordServerDate } from '../../shared/services/serverClock';

type DynamicBaseQueryArgs = FetchArgs & {
  service?: string;
};

const SERVICE_URL_MAP: Record<string, string> = {
  php1: ENV_CONFIG.php1Url,
  php3: ENV_CONFIG.php3Url,
  djangoTnEmis: ENV_CONFIG.djangoTnEmisUrl,
  djangoEmis: ENV_CONFIG.djangoEmisUrl,
  djangoDashboard: ENV_CONFIG.djangoDashboardUrl,
  login: ENV_CONFIG.loginUrl,
  emis_login: ENV_CONFIG.emis_login,
};

const getApiErrorMessage = (status: number | string, data: any) => {
  const backendMessage =
    data?.message ||
    data?.error ||
    data?.detail ||
    data?.msg ||
    (typeof data === 'string' ? data : '');

  switch (status) {
    case 'FETCH_ERROR':
      return (
        backendMessage ||
        'Unable to connect to server. Please check your network or server status.'
      );

    case 0:
      return 'Unable to connect to server. Please check your internet connection or server status.';

    case 400:
      return (
        backendMessage || 'Invalid request. Please check the submitted data.'
      );

    case 401:
      return backendMessage || 'Your session has expired. Please login again.';

    case 403:
      return (
        backendMessage || 'You do not have permission to perform this action.'
      );

    case 404:
      return backendMessage || 'Requested data was not found.';

    case 408:
      return backendMessage || 'Request timeout. Please try again.';

    case 422:
      return (
        backendMessage || 'Validation failed. Please check the entered details.'
      );

    case 429:
      return (
        backendMessage || 'Too many requests. Please try again after some time.'
      );

    case 500:
      return 'Internal server error occurred.';

    case 502:
      return (
        backendMessage || 'Bad gateway. Server is not responding properly.'
      );

    case 503:
      return (
        backendMessage ||
        'Service temporarily unavailable. Please try again later.'
      );

    case 504:
      return (
        backendMessage || 'Gateway timeout. Server took too long to respond.'
      );

    default:
      return backendMessage || 'Something went wrong. Please try again later.';
  }
};

export const dynamicBaseQuery: BaseQueryFn<
  string | DynamicBaseQueryArgs,
  unknown,
  FetchBaseQueryError
> = async (args, _api, _extraOptions) => {
  let service: string = 'php1';
  let modifiedArgs: FetchArgs;

  if (typeof args === 'string') {
    modifiedArgs = { url: args };
  } else {
    const { service: extractedService, ...restArgs } = args;
    service = extractedService ?? 'php1';
    modifiedArgs = restArgs;
  }

  const baseUrl = SERVICE_URL_MAP[service];

  const {
    url,
    method = 'GET',
    body,
    params,
    headers: argHeaders,
  } = modifiedArgs;

  const authKey = localStorage.getItem('AuthorizationKey');
  const token = localStorage.getItem('token');
  const state = _api.getState() as RootState;
  console.log(url, 'url');
  const SKIP_SCOPE_URLS = ['datastamp', 'alldistrictlist', 'commonmasters'];
  const applyScope = !SKIP_SCOPE_URLS.some((u) => url.includes(u));
  console.log(applyScope, 'applyScope');
  const s = state.session;
  try {
    const result = await axios({
      url: `${baseUrl}/${url}`,
      method,
      data: body,
      params: {
        ...(applyScope &&
          s['user_role_level'] === 'district' && {
            district_id: s['districtId'],
          }),
        ...(applyScope &&
          s['user_role_level'] === 'block' && {
            block_id: s['blockId'],
          }),
        ...params,
      },
      headers: {
        Accept: 'application/json, text/plain, */*',
        ...(authKey && { Authorization: authKey }),
        ...(token && { Token: token }),
        ...(argHeaders as Record<string, string>),
        ...(url == 'testnewlogin' && { 'X-Auth-Type': '1' }),
      },
    });

    // Capture the server's clock from the response `Date` header so exports can
    // stamp SERVER time instead of the (spoofable) local clock.
    recordServerDate(
      (result.headers?.date as string | undefined) ??
        (result.headers?.Date as string | undefined)
    );

    return { data: result.data };
  } catch (error: any) {
    // Error responses still carry a Date header — keep the server clock fresh.
    recordServerDate(error.response?.headers?.date);
    const status = error.response?.status ?? 'FETCH_ERROR';
    const responseData = error.response?.data ?? error.message;

    const errorMessage = getApiErrorMessage(status, responseData);
    toast.error(errorMessage);

    console.error('API Error:', {
      status,
      url: error.config?.url,
      method: error.config?.method,
      data: responseData,
      message: errorMessage,
    });

    if (status === 401) {
      // A 401 is usually a stale OAuth Authorization key, not a dead session.
      // Previously this did localStorage.clear(), logging the user out on any
      // transient 401. Instead, drop the stale key + its expiry stamp and
      // refetch a fresh one (fire-and-forget) so the next request/poll recovers
      // without forcing a re-login. The session token is left untouched.
      localStorage.removeItem('AuthorizationKey');
      localStorage.removeItem('AuthorizationKeyExpiry');
      void fetchOAuthKey();
    }

    return {
      error: {
        status,
        data: {
          originalError: responseData,
          message: errorMessage,
        },
      } as FetchBaseQueryError,
    };
  }
};
